import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Grid, Divider, Chip, OutlinedInput, InputAdornment, SelectChangeEvent } from '@mui/material';
import { HotelFormData } from '@types';

// --- Constants (Can be imported or defined here) ---
const propertyTypes = ['Full Service', 'Limited Service', 'Extended Stay', 'Resort', 'Boutique', 'Conference Center', 'Casino', 'Mixed Use'];
const commonAmenities = [
  'Restaurant',
  'Pool',
  'Fitness Center',
  'Business Center',
  'Meeting Rooms',
  'Spa',
  'Room Service',
  'Parking',
  'Airport Shuttle',
  'Pet Friendly',
  'Free WiFi',
  'Bar/Lounge',
  'Concierge',
];

const BasicInfoStep: React.FC = () => {
  // Get methods from FormProvider context
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<HotelFormData>();

  return (
    <Box sx={{ mt: 1 }}>
      {/* --- General Information Sub-section --- */}
      <Divider sx={{ my: 2 }}>
        <Chip label="General Information" />
      </Divider>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label="Hotel Name" fullWidth required {...register('name', { required: 'Hotel Name is required.' })} error={!!errors.name} helperText={errors.name?.message} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="property_type"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select {...field} label="Property Type">
                  {propertyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Chain Affiliation" fullWidth {...register('chain_affiliation')} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Website"
            type="url"
            fullWidth
            {...register('website')}
            error={!!errors.website} // Add validation if needed in useForm setup
            helperText={errors.website?.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Description" fullWidth multiline rows={3} {...register('description')} />
        </Grid>
      </Grid>

      {/* --- Location Sub-section --- */}
      <Divider sx={{ my: 2 }}>
        <Chip label="Location" />
      </Divider>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label="Street Address" fullWidth {...register('address')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="City" fullWidth required {...register('city', { required: 'City is required.' })} error={!!errors.city} helperText={errors.city?.message} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="State / Province" fullWidth required {...register('state', { required: 'State/Province is required.' })} error={!!errors.state} helperText={errors.state?.message} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="ZIP / Postal Code" fullWidth {...register('zip_code')} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Country"
            fullWidth
            defaultValue="USA" // Default value handled in useForm
            {...register('country')}
          />
        </Grid>
      </Grid>

      {/* --- Specifications Sub-section --- */}
      <Divider sx={{ my: 2 }}>
        <Chip label="Specifications" />
      </Divider>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Number of Rooms"
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 0 } }}
            {...register('num_rooms', {
              valueAsNumber: false,
              min: { value: 0, message: 'Cannot be negative' },
            })}
            error={!!errors.num_rooms}
            helperText={errors.num_rooms?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Star Rating"
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 0, max: 5, step: 0.5 } }}
            {...register('star_rating', {
              valueAsNumber: false,
              min: { value: 0, message: 'Min rating is 0' },
              max: { value: 5, message: 'Max rating is 5' },
            })}
            error={!!errors.star_rating}
            helperText={errors.star_rating?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Year Built"
            type="number"
            fullWidth
            InputProps={{ inputProps: { min: 1800, max: new Date().getFullYear() } }}
            {...register('year_built', {
              valueAsNumber: false,
              min: { value: 1800, message: 'Invalid year' },
              max: { value: new Date().getFullYear(), message: 'Year cannot be in the future' },
            })}
            error={!!errors.year_built}
            helperText={errors.year_built?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Lot Size"
            type="number"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">sq ft</InputAdornment>,
              inputProps: { min: 0 },
            }}
            {...register('lot_size', {
              valueAsNumber: true,
              min: { value: 0, message: 'Cannot be negative' },
            })}
            error={!!errors.lot_size}
            helperText={errors.lot_size?.message}
          />
        </Grid>
      </Grid>

      {/* --- Amenities & Notes Sub-section --- */}
      <Divider sx={{ my: 2 }}>
        <Chip label="Amenities & Notes" />
      </Divider>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {/* Controller for MUI Multi-Select */}
          <Controller
            name="amenities"
            control={control}
            defaultValue={[]} // Default to empty array
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Amenities</InputLabel>
                <Select
                  multiple
                  {...field} // Spread field props (includes value, onChange, ref)
                  input={<OutlinedInput label="Amenities" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {/* Ensure selected is always an array */}
                      {(Array.isArray(selected) ? selected : []).map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {commonAmenities.map((amenity) => (
                    <MenuItem key={amenity} value={amenity}>
                      {amenity}
                    </MenuItem>
                  ))}
                  {/* Optionally allow adding custom amenities */}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Notes" fullWidth multiline rows={4} placeholder="Additional information about the hotel, condition, unique features..." {...register('notes')} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfoStep;
